import { useEffect, useState } from 'react';
import {
  HoaDonDieuChinhThongTin,
  HoaDonDieuChinhThongTinInput,
  HoaDonDieuChinhThongTinResponse
} from '@/types/schemas/hoa-don-dieu-chinh-thong-tin.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseHoaDonDieuChinhThongTinReturn {
  hoaDonDieuChinhThongTins: HoaDonDieuChinhThongTin[];
  isLoading: boolean;
  addHoaDonDieuChinhThongTin: (
    newHoaDonDieuChinhThongTin: HoaDonDieuChinhThongTinInput
  ) => Promise<HoaDonDieuChinhThongTin>;
  updateHoaDonDieuChinhThongTin: (
    uuid: string,
    updatedHoaDonDieuChinhThongTin: HoaDonDieuChinhThongTinInput
  ) => Promise<HoaDonDieuChinhThongTin>;
  deleteHoaDonDieuChinhThongTin: (uuid: string) => Promise<void>;
  refreshHoaDonDieuChinhThongTins: () => Promise<void>;
  getHoaDonDieuChinhThongTinByUuid: (uuid: string) => Promise<HoaDonDieuChinhThongTin | null>;
  searchHoaDonDieuChinhThongTins: (query: string) => Promise<HoaDonDieuChinhThongTin[]>;
}

/**
 * Hook for managing HoaDonDieuChinhThongTin (Invoice Information Adjustment) data
 *
 * This hook provides functions to fetch, create, update, and delete invoice information adjustments.
 * It follows the established pattern for ERP data management hooks.
 */
export const useHoaDonDieuChinhThongTin = (
  initialHoaDonDieuChinhThongTins: HoaDonDieuChinhThongTin[] = []
): UseHoaDonDieuChinhThongTinReturn => {
  const [hoaDonDieuChinhThongTins, setHoaDonDieuChinhThongTins] = useState<HoaDonDieuChinhThongTin[]>(
    initialHoaDonDieuChinhThongTins
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchHoaDonDieuChinhThongTins = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonDieuChinhThongTinResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_THONG_TIN}/`
      );
      setHoaDonDieuChinhThongTins(response.data.results);
    } catch (error) {
      console.error('Error fetching invoice information adjustments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addHoaDonDieuChinhThongTin = async (
    newHoaDonDieuChinhThongTin: HoaDonDieuChinhThongTinInput
  ): Promise<HoaDonDieuChinhThongTin> => {
    if (!entity?.slug) {
      throw new Error('Entity slug is required');
    }

    try {
      const response = await api.post<HoaDonDieuChinhThongTin>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_THONG_TIN}/`,
        newHoaDonDieuChinhThongTin
      );

      const createdHoaDonDieuChinhThongTin = response.data;
      setHoaDonDieuChinhThongTins(prev => [createdHoaDonDieuChinhThongTin, ...prev]);
      return createdHoaDonDieuChinhThongTin;
    } catch (error) {
      console.error('Error creating invoice information adjustment:', error);
      throw error;
    }
  };

  const updateHoaDonDieuChinhThongTin = async (
    uuid: string,
    updatedHoaDonDieuChinhThongTin: HoaDonDieuChinhThongTinInput
  ): Promise<HoaDonDieuChinhThongTin> => {
    if (!entity?.slug) {
      throw new Error('Entity slug is required');
    }

    try {
      const response = await api.put<HoaDonDieuChinhThongTin>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_THONG_TIN}/${uuid}/`,
        updatedHoaDonDieuChinhThongTin
      );

      const updatedItem = response.data;
      setHoaDonDieuChinhThongTins(prev => prev.map(item => (item.uuid === uuid ? updatedItem : item)));
      return updatedItem;
    } catch (error) {
      console.error('Error updating invoice information adjustment:', error);
      throw error;
    }
  };

  const deleteHoaDonDieuChinhThongTin = async (uuid: string): Promise<void> => {
    if (!entity?.slug) {
      throw new Error('Entity slug is required');
    }

    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_THONG_TIN}/${uuid}/`);

      setHoaDonDieuChinhThongTins(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting invoice information adjustment:', error);
      throw error;
    }
  };

  const refreshHoaDonDieuChinhThongTins = async (): Promise<void> => {
    await fetchHoaDonDieuChinhThongTins();
  };

  const getHoaDonDieuChinhThongTinByUuid = async (uuid: string): Promise<HoaDonDieuChinhThongTin | null> => {
    if (!entity?.slug) return null;

    try {
      // First check if it exists in local state
      const found = hoaDonDieuChinhThongTins.find(item => item.uuid === uuid);
      if (found) return found;

      // If not found in local state, fetch from API
      const response = await api.get<HoaDonDieuChinhThongTin>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_THONG_TIN}/${uuid}/`
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching invoice information adjustment by UUID:', error);
      return null;
    }
  };

  const searchHoaDonDieuChinhThongTins = async (query: string): Promise<HoaDonDieuChinhThongTin[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<HoaDonDieuChinhThongTinResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_THONG_TIN}/search/`,
        {
          params: { q: query }
        }
      );

      return response.data.results;
    } catch (error) {
      console.error('Error searching invoice information adjustments:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchHoaDonDieuChinhThongTins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    hoaDonDieuChinhThongTins,
    isLoading,
    addHoaDonDieuChinhThongTin,
    updateHoaDonDieuChinhThongTin,
    deleteHoaDonDieuChinhThongTin,
    refreshHoaDonDieuChinhThongTins,
    getHoaDonDieuChinhThongTinByUuid,
    searchHoaDonDieuChinhThongTins
  };
};
